/*
 * @bot-written
 *
 * WARNING AND NOTICE
 * Any access, download, storage, and/or use of this source code is subject to the terms and conditions of the
 * Full Software Licence as accepted by you before being granted access to this source code and other materials,
 * the terms of which can be accessed on the Codebots website at https://codebots.com/full-software-licence. Any
 * commercial use in contravention of the terms of the Full Software Licence may be pursued by Codebots through
 * licence termination and further legal action, and be required to indemnify Codebots for any loss or damage,
 * including interest and costs. You are deemed to have accepted the terms of the Full Software Licence on any
 * access, download, storage, and/or use of this source code.
 *
 * BOT WARNING
 * This file is bot-written.
 * Any changes out side of "protected regions" will be lost next time the bot makes any changes.
 */
import { action, observable } from 'mobx';
import { Model, IModelAttributes, attribute, entity } from 'Models/Model';
import * as Models from 'Models/Entities';
import * as Validators from 'Validators';
import { CRUD } from '../CRUDOptions';
import * as AttrUtils from "Util/AttributeUtils";
import { IAcl } from 'Models/Security/IAcl';
import {
	makeFetchOneToManyFunc,
	getCreatedModifiedCrudOptions,
} from 'Util/EntityUtils';
import { VisitorsPartEntity } from 'Models/Security/Acl/VisitorsPartEntity';
import { AptusAdminPartEntity } from 'Models/Security/Acl/AptusAdminPartEntity';
import { UserPartEntity } from 'Models/Security/Acl/UserPartEntity';
import { EntityFormMode } from 'Views/Components/Helpers/Common';
import {SuperAdministratorScheme} from '../Security/Acl/SuperAdministratorScheme';
// % protected region % [Add any further imports here] off begin
// % protected region % [Add any further imports here] end

export interface IPartEntityAttributes extends IModelAttributes {
	assemblyName: string;
	mass: number;

	countryPartss: Array<Models.CountryPartEntity | Models.ICountryPartEntityAttributes>;
	priceVersionId?: string;
	priceVersion?: Models.PriceVersionEntity | Models.IPriceVersionEntityAttributes;
	// % protected region % [Add any custom attributes to the interface here] off begin
	// % protected region % [Add any custom attributes to the interface here] end
}

// % protected region % [Customise your entity metadata here] off begin
@entity('PartEntity', 'Part')
// % protected region % [Customise your entity metadata here] end
export default class PartEntity extends Model implements IPartEntityAttributes {
	public static acls: IAcl[] = [
		new SuperAdministratorScheme(),
		new VisitorsPartEntity(),
		new AptusAdminPartEntity(),
		new UserPartEntity(),
		// % protected region % [Add any further ACL entries here] off begin
		// % protected region % [Add any further ACL entries here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in create operations.
	 */
	public static excludeFromCreate: string[] = [
		// % protected region % [Add any custom create exclusions here] off begin
		// % protected region % [Add any custom create exclusions here] end
	];

	/**
	 * Fields to exclude from the JSON serialization in update operations.
	 */
	public static excludeFromUpdate: string[] = [
		// % protected region % [Add any custom update exclusions here] off begin
		// % protected region % [Add any custom update exclusions here] end
	];

	// % protected region % [Modify props to the crud options here for attribute 'Assembly Name'] off begin
	/**
	 * Aptus part code number in catalogue
	 */
	@Validators.Required()
	@observable
	@attribute()
	@CRUD({
		name: 'Assembly Name',
		displayType: 'textfield',
		order: 10,
		headerColumn: true,
		searchable: true,
		searchFunction: 'like',
		searchTransform: AttrUtils.standardiseString,
	})
	public assemblyName: string;
	// % protected region % [Modify props to the crud options here for attribute 'Assembly Name'] end

	// % protected region % [Modify props to the crud options here for attribute 'Mass'] off begin
	@Validators.Numeric()
	@observable
	@attribute()
	@CRUD({
		name: 'Mass',
		displayType: 'textfield',
		order: 20,
		headerColumn: true,
		searchable: true,
		searchFunction: 'equal',
		searchTransform: AttrUtils.standardiseFloat,
	})
	public mass: number;
	// % protected region % [Modify props to the crud options here for attribute 'Mass'] end

	@observable
	@attribute({isReference: true})
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Country Parts'] off begin
		name: "Country Partss",
		displayType: 'reference-multicombobox',
		order: 30,
		referenceTypeFunc: () => Models.CountryPartEntity,
		referenceResolveFunction: makeFetchOneToManyFunc({
			relationName: 'countryPartss',
			oppositeEntity: () => Models.CountryPartEntity,
		}),
		// % protected region % [Modify props to the crud options here for reference 'Country Parts'] end
	})
	public countryPartss: Models.CountryPartEntity[] = [];

	/**
	 * Part price list for different versions
	 */
	@observable
	@attribute()
	@CRUD({
		// % protected region % [Modify props to the crud options here for reference 'Price Version'] off begin
		name: 'Price Version',
		displayType: 'reference-combobox',
		order: 40,
		referenceTypeFunc: () => Models.PriceVersionEntity,
		// % protected region % [Modify props to the crud options here for reference 'Price Version'] end
	})
	public priceVersionId?: string;
	@observable
	@attribute({isReference: true})
	public priceVersion: Models.PriceVersionEntity;

	// % protected region % [Add any custom attributes to the model here] off begin
	// % protected region % [Add any custom attributes to the model here] end

	// eslint-disable-next-line @typescript-eslint/no-useless-constructor
	constructor(attributes?: Partial<IPartEntityAttributes>) {
		// % protected region % [Add any extra constructor logic before calling super here] off begin
		// % protected region % [Add any extra constructor logic before calling super here] end

		super(attributes);

		// % protected region % [Add any extra constructor logic after calling super here] off begin
		// % protected region % [Add any extra constructor logic after calling super here] end
	}

	/**
	 * Assigns fields from a passed in JSON object to the fields in this model.
	 * Any reference objects that are passed in are converted to models if they are not already.
	 * This function is called from the constructor to assign the initial fields.
	 */
	@action
	public assignAttributes(attributes?: Partial<IPartEntityAttributes>) {
		// % protected region % [Override assign attributes here] off begin
		super.assignAttributes(attributes);

		if (attributes) {
			if (attributes.assemblyName !== undefined) {
				this.assemblyName = attributes.assemblyName;
			}
			if (attributes.mass !== undefined) {
				this.mass = attributes.mass;
			}
			if (attributes.countryPartss !== undefined && Array.isArray(attributes.countryPartss)) {
				for (const model of attributes.countryPartss) {
					if (model instanceof Models.CountryPartEntity) {
						this.countryPartss.push(model);
					} else {
						this.countryPartss.push(new Models.CountryPartEntity(model));
					}
				}
			}
			if (attributes.priceVersionId !== undefined) {
				this.priceVersionId = attributes.priceVersionId;
			}
			if (attributes.priceVersion !== undefined) {
				if (attributes.priceVersion === null) {
					this.priceVersion = attributes.priceVersion;
				} else {
					if (attributes.priceVersion instanceof Models.PriceVersionEntity) {
						this.priceVersion = attributes.priceVersion;
						this.priceVersionId = attributes.priceVersion.id;
					} else {
						this.priceVersion = new Models.PriceVersionEntity(attributes.priceVersion);
						this.priceVersionId = this.priceVersion.id;
					}
				}
			}
			// % protected region % [Override assign attributes here] end

			// % protected region % [Add any extra assign attributes logic here] off begin
			// % protected region % [Add any extra assign attributes logic here] end
		}
	}

	/**
	 * Additional fields that are added to GraphQL queries when using the
	 * the managed model APIs.
	 */
	// % protected region % [Customize Default Expands here] off begin
	public defaultExpands = `
		countryPartss {
			${Models.CountryPartEntity.getAttributes().join('\n')}
		}
		priceVersion {
			${Models.PriceVersionEntity.getAttributes().join('\n')}
		}
	`;
	// % protected region % [Customize Default Expands here] end

	/**
	 * The save method that is called from the admin CRUD components.
	 */
	// % protected region % [Customize Save From Crud here] off begin
	public async saveFromCrud(formMode: EntityFormMode) {
		const relationPath = {
			countryPartss: {},
		};
		return this.save(
			relationPath,
			{
				options: [
					{
						key: 'mergeReferences',
						graphQlType: '[String]',
						value: [
							'countryPartss',
						]
					},
				],
			}
		);
	}
	// % protected region % [Customize Save From Crud here] end

	/**
	 * Returns the string representation of this entity to display on the UI.
	 */
	public getDisplayName() {
		// % protected region % [Customise the display name for this entity] on begin
		return this.assemblyName;
		// % protected region % [Customise the display name for this entity] end
	}


	// % protected region % [Add any further custom model features here] off begin
	// % protected region % [Add any further custom model features here] end
}

// % protected region % [Modify the create and modified CRUD attributes here] off begin
/*
 * Retrieve the created and modified CRUD attributes for defining the CRUD views and decorate the class with them.
 */
const [ createdAttr, modifiedAttr ] = getCreatedModifiedCrudOptions();
CRUD(createdAttr)(PartEntity.prototype, 'created');
CRUD(modifiedAttr)(PartEntity.prototype, 'modified');
// % protected region % [Modify the create and modified CRUD attributes here] end
